import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { AgentAvatar } from './AgentAvatars';
import { Shield, Database, Users, FileText, AlertTriangle, CheckCircle, Activity, Plus, Edit, Trash2, Send, Eye, X, Save } from 'lucide-react';

export default function AdminPanel() {
  const [health, setHealth] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('health');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ agent_type: '', task_id: '', name: '', description: '', system_prompt: '', user_prompt: '' });
  const [saving, setSaving] = useState(false);
  const [subsOverview, setSubsOverview] = useState(null);
  const [editingPricing, setEditingPricing] = useState(false);
  const [pricingForm, setPricingForm] = useState({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [healthRes, logsRes, templatesRes, subsRes] = await Promise.all([
        api.get('/api/admin/health'),
        api.get('/api/admin/audit-logs'),
        api.get('/api/admin/prompt-templates'),
        api.get('/api/admin/subscriptions/overview').catch(e => ({ data: null })),
      ]);
      setHealth(healthRes.data);
      setAuditLogs(logsRes.data || []);
      setTemplates(templatesRes.data || []);
      if (subsRes.data) {
        setSubsOverview(subsRes.data);
        const pf = {};
        (subsRes.data.plans || []).forEach(p => { pf[p.id] = p.price; });
        setPricingForm(pf);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      if (editingTemplate) {
        await api.put(`/api/admin/prompt-templates/${editingTemplate.id}`, {
          name: templateForm.name, description: templateForm.description,
          system_prompt: templateForm.system_prompt, user_prompt: templateForm.user_prompt
        });
      } else {
        await api.post('/api/admin/prompt-templates', templateForm);
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateForm({ agent_type: '', task_id: '', name: '', description: '', system_prompt: '', user_prompt: '' });
      loadData();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handlePublish = async (id) => {
    try {
      await api.post(`/api/admin/prompt-templates/${id}/publish`);
      loadData();
    } catch (err) { console.error(err); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this prompt template?')) return;
    try {
      await api.delete(`/api/admin/prompt-templates/${id}`);
      loadData();
    } catch (err) { console.error(err); }
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      agent_type: template.agent_type, task_id: template.task_id,
      name: template.name, description: template.description || '',
      system_prompt: template.system_prompt, user_prompt: template.user_prompt
    });
    setShowTemplateModal(true);
  };

  const handleSavePricing = async () => {
    try {
      const planUpdates = {};
      Object.entries(pricingForm).forEach(([id, price]) => { planUpdates[id] = { price: Number(price) }; });
      await api.put('/api/owner/subscription-plans', { plans: planUpdates });
      setEditingPricing(false);
      loadData();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  const AGENT_TYPES = [
    { id: 'kathy', name: 'Kathy' }, { id: 'sophie', name: 'Sophie' },
    { id: 'john', name: 'John' }, { id: 'omar', name: 'Omar' }
  ];

  return (
    <div className="admin-page fade-in">
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Admin Panel</h3>
        <p style={{ fontSize: 14, color: '#64748b' }}>Monitor system health, manage prompt templates, view audit logs, and configure integrations.</p>
      </div>

      <div className="ws-tabs" style={{ background: 'white', borderRadius: '14px 14px 0 0', border: '1px solid #e2e8f0', borderBottom: 'none', marginBottom: 0 }}>
        <div className={`ws-tab ${activeTab === 'health' ? 'active' : ''}`} onClick={() => setActiveTab('health')} data-testid="admin-tab-health">
          <Activity size={14} style={{ marginRight: 6 }} />System Health
        </div>
        <div className={`ws-tab ${activeTab === 'prompts' ? 'active' : ''}`} onClick={() => setActiveTab('prompts')} data-testid="admin-tab-prompts">
          <Edit size={14} style={{ marginRight: 6 }} />Prompt Templates
        </div>
        <div className={`ws-tab ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')} data-testid="admin-tab-audit">
          <Shield size={14} style={{ marginRight: 6 }} />Audit Logs
        </div>
        <div className={`ws-tab ${activeTab === 'subscriptions' ? 'active' : ''}`} onClick={() => setActiveTab('subscriptions')} data-testid="admin-tab-subs">
          <FileText size={14} style={{ marginRight: 6 }} />Subscriptions
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '0 0 14px 14px', border: '1px solid #e2e8f0', padding: 24, minHeight: 400 }}>
        {/* Health Tab */}
        {activeTab === 'health' && health && (
          <div className="admin-grid">
            <div className="admin-stat">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Database size={18} color="#2E7D32" /><span className="label">Database</span>
              </div>
              <span className={`status-badge ${health.db_status === 'healthy' ? 'status-healthy' : 'status-error'}`}>
                {health.db_status === 'healthy' ? <><CheckCircle size={14} style={{ marginRight: 4 }} />Healthy</> : 'Error'}
              </span>
            </div>
            <div className="admin-stat">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Activity size={18} color="#7B1FA2" /><span className="label">LLM Service</span>
              </div>
              <span className={`status-badge ${health.llm_status === 'healthy' ? 'status-healthy' : 'status-error'}`}>
                {health.llm_status === 'healthy' ? <><CheckCircle size={14} style={{ marginRight: 4 }} />Connected</> : 'No Key'}
              </span>
            </div>
            <div className="admin-stat"><div className="value" style={{ color: '#2E7D32' }}>{health.total_agents}</div><div className="label">Active Agents</div></div>
            <div className="admin-stat"><div className="value" style={{ color: '#7B1FA2' }}>{health.total_users}</div><div className="label">Total Users</div></div>
            <div className="admin-stat"><div className="value" style={{ color: '#1565C0' }}>{health.total_tasks}</div><div className="label">Total Tasks</div></div>
            <div className="admin-stat"><div className="value" style={{ color: '#F9A825' }}>{health.total_documents}</div><div className="label">Documents</div></div>
            <div className="admin-stat">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={18} color={health.failed_tasks > 0 ? '#dc2626' : '#22c55e'} />
                <div>
                  <div className="value" style={{ color: health.failed_tasks > 0 ? '#dc2626' : '#22c55e' }}>{health.failed_tasks}</div>
                  <div className="label">Failed Tasks</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Prompt Templates Tab */}
        {activeTab === 'prompts' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h4 style={{ fontSize: 16, fontWeight: 700 }}>Prompt Templates</h4>
              <button className="btn-action" onClick={() => { setEditingTemplate(null); setTemplateForm({ agent_type: '', task_id: '', name: '', description: '', system_prompt: '', user_prompt: '' }); setShowTemplateModal(true); }} data-testid="create-template-btn">
                <Plus size={16} style={{ marginRight: 6 }} />Create Template
              </button>
            </div>
            {templates.length === 0 ? (
              <div className="empty-state">
                <Edit size={64} />
                <h4>No Prompt Templates Yet</h4>
                <p>Create custom prompt templates to fine-tune how your AI employees respond and perform tasks.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {templates.map(t => (
                  <div key={t.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} data-testid={`template-${t.id}`}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <AgentAvatar agentType={t.agent_type} size={28} />
                        <h4 style={{ fontSize: 14, fontWeight: 700 }}>{t.name}</h4>
                        {t.published && <span className="status-badge status-healthy" style={{ fontSize: 10 }}>Published</span>}
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>v{t.version}</span>
                      </div>
                      <p style={{ fontSize: 12, color: '#64748b' }}>{t.description || 'No description'} | Agent: {t.agent_type} | Task: {t.task_id}</p>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {!t.published && (
                        <button onClick={() => handlePublish(t.id)} className="btn-action" style={{ padding: '6px 12px', fontSize: 12 }} data-testid={`publish-${t.id}`}>
                          <Send size={12} style={{ marginRight: 4 }} />Publish
                        </button>
                      )}
                      <button onClick={() => openEditModal(t)} className="btn-secondary" style={{ padding: '6px 12px', fontSize: 12 }} data-testid={`edit-${t.id}`}>
                        <Edit size={12} style={{ marginRight: 4 }} />Edit
                      </button>
                      <button onClick={() => handleDeleteTemplate(t.id)} className="btn-delete" style={{ padding: '6px 12px', fontSize: 12 }} data-testid={`delete-template-${t.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Audit Logs Tab */}
        {activeTab === 'audit' && (
          <>
            <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Audit Logs</h4>
            {auditLogs.length === 0 ? (
              <div className="empty-state"><p>No audit logs yet.</p></div>
            ) : (
              <table className="audit-log-table" data-testid="audit-log-table">
                <thead><tr><th>Timestamp</th><th>Action</th><th>Resource</th><th>Details</th></tr></thead>
                <tbody>
                  {auditLogs.slice(0, 50).map((log, i) => (
                    <tr key={log.id || i}>
                      <td>{new Date(log.timestamp).toLocaleString()}</td>
                      <td><span style={{ fontWeight: 600 }}>{log.action}</span></td>
                      <td>{log.resource_type}</td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>{JSON.stringify(log.details || {}).slice(0, 80)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* Subscriptions Tab (Live) */}
        {activeTab === 'subscriptions' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h4 style={{ fontSize: 16, fontWeight: 700 }}>Subscription Management</h4>
              <button className={editingPricing ? 'btn-secondary' : 'btn-action'} onClick={() => setEditingPricing(!editingPricing)} style={{ fontSize: 12, padding: '6px 14px' }}>
                {editingPricing ? 'Cancel' : 'Edit Pricing'}
              </button>
            </div>

            {/* Summary Stats */}
            {subsOverview && (
              <div className="admin-grid" style={{ marginBottom: 20 }}>
                <div className="admin-stat" style={{ borderTop: '4px solid var(--panzix-green)' }}>
                  <div className="value" style={{ color: 'var(--panzix-green)' }}>${subsOverview.total_mrr}</div>
                  <div className="label">Monthly Revenue (MRR)</div>
                </div>
                <div className="admin-stat" style={{ borderTop: '4px solid var(--panzix-purple)' }}>
                  <div className="value" style={{ color: 'var(--panzix-purple)' }}>${subsOverview.total_arr}</div>
                  <div className="label">Annual Revenue (ARR)</div>
                </div>
                <div className="admin-stat" style={{ borderTop: '4px solid #1565C0' }}>
                  <div className="value" style={{ color: '#1565C0' }}>{subsOverview.total_active}</div>
                  <div className="label">Active Subscribers</div>
                </div>
              </div>
            )}

            {/* Plan Cards */}
            <div className="admin-grid">
              {(subsOverview?.plans || []).map(plan => (
                <div key={plan.id} className="admin-stat" style={{ borderTop: `4px solid ${plan.id === 'enterprise' ? '#F9A825' : plan.id === 'professional' ? '#7B1FA2' : '#2E7D32'}` }}>
                  <h4 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{plan.name}</h4>
                  {editingPricing ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, margin: '8px 0' }}>
                      <span style={{ fontSize: 18, fontWeight: 700, color: '#64748b' }}>$</span>
                      <input type="number" value={pricingForm[plan.id] || plan.price} onChange={e => setPricingForm(f => ({ ...f, [plan.id]: e.target.value }))}
                        style={{ width: 80, padding: '6px 10px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 18, fontWeight: 700, textAlign: 'center' }} />
                      <span style={{ fontSize: 13, color: '#94a3b8' }}>/mo</span>
                    </div>
                  ) : (
                    <div className="value" style={{ color: plan.id === 'enterprise' ? '#F9A825' : plan.id === 'professional' ? '#7B1FA2' : '#2E7D32', fontSize: 28 }}>${plan.price}/mo</div>
                  )}
                  <p style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>{plan.agents} AI Employees</p>
                  <p style={{ fontSize: 14, fontWeight: 700, marginTop: 8, color: '#0f172a' }}>{plan.subscriber_count} subscribers</p>
                </div>
              ))}
            </div>
            {editingPricing && (
              <button className="btn-action" onClick={handleSavePricing} style={{ width: '100%', marginTop: 12, borderRadius: 10 }}>Save Updated Pricing</button>
            )}

            {/* Recent Subscriptions */}
            {subsOverview?.recent_subscriptions?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Recent Subscriptions</h4>
                <table className="audit-log-table">
                  <thead><tr><th>Company</th><th>Plan</th><th>Price</th><th>Status</th><th>Started</th></tr></thead>
                  <tbody>
                    {subsOverview.recent_subscriptions.map((s, i) => (
                      <tr key={s.id || i}>
                        <td style={{ fontWeight: 600 }}>{s.company_name}</td>
                        <td>{s.plan_name || s.plan}</td>
                        <td style={{ fontWeight: 600, color: 'var(--panzix-green)' }}>${s.price}/mo</td>
                        <td><span className={`agent-status ${s.status === 'active' || s.status === 'trialing' ? 'active' : 'paused'}`} style={{ fontSize: 11 }}>{s.status}</span></td>
                        <td style={{ fontSize: 12 }}>{new Date(s.started_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!subsOverview?.total_active && !editingPricing && (
              <p style={{ textAlign: 'center', color: '#94a3b8', padding: 20, fontSize: 14 }}>No active subscribers yet. Pricing is live on the landing page.</p>
            )}
          </>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setShowTemplateModal(false)}>
          <div className="task-modal slide-up" style={{ maxWidth: 640 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3>{editingTemplate ? 'Edit Template' : 'Create Prompt Template'}</h3>
              <button onClick={() => setShowTemplateModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="form-group">
              <label>Template Name</label>
              <input data-testid="template-name" value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Enhanced Onboarding v2" />
            </div>
            {!editingTemplate && (
              <div style={{ display: 'flex', gap: 12 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Agent Type</label>
                  <select data-testid="template-agent" value={templateForm.agent_type} onChange={e => setTemplateForm(f => ({ ...f, agent_type: e.target.value }))}
                    style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14 }}>
                    <option value="">Select agent...</option>
                    {AGENT_TYPES.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Task ID</label>
                  <input data-testid="template-task-id" value={templateForm.task_id} onChange={e => setTemplateForm(f => ({ ...f, task_id: e.target.value }))} placeholder="e.g., onboarding_checklist" />
                </div>
              </div>
            )}
            <div className="form-group">
              <label>Description</label>
              <input value={templateForm.description} onChange={e => setTemplateForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description of what this template does" />
            </div>
            <div className="form-group">
              <label>System Prompt</label>
              <textarea data-testid="template-system-prompt" value={templateForm.system_prompt} onChange={e => setTemplateForm(f => ({ ...f, system_prompt: e.target.value }))}
                placeholder="The system-level instructions for the AI agent..." style={{ width: '100%', minHeight: 100, padding: 12, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'monospace', resize: 'vertical' }} />
            </div>
            <div className="form-group">
              <label>User Prompt Template</label>
              <textarea data-testid="template-user-prompt" value={templateForm.user_prompt} onChange={e => setTemplateForm(f => ({ ...f, user_prompt: e.target.value }))}
                placeholder="The prompt template with {variable} placeholders..." style={{ width: '100%', minHeight: 100, padding: 12, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'monospace', resize: 'vertical' }} />
            </div>
            <button className="btn-action" onClick={handleSaveTemplate} disabled={saving || !templateForm.name || !templateForm.system_prompt}
              style={{ width: '100%' }} data-testid="save-template-btn">
              <Save size={16} style={{ marginRight: 6 }} />{saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
